import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';
import { useAdminBasePath } from '../../hooks/useAdminBasePath';
import { ENTERPRISE_ADMIN_BASE } from '../../config/adminLayers';
import type { AdminWorkspaceSubscriptionRow } from '../../types';
import { SubscriptionStatusBadge } from './components/SubscriptionStatusBadge';
import { SubscriptionEditModal } from './components/SubscriptionEditModal';

const PLAN_OPTIONS = ['', 'individual', 'starter', 'business', 'enterprise'];

type SubscriptionTab = 'active' | 'expired' | 'trial';

const SUBSCRIPTION_TABS: { id: SubscriptionTab; label: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'expired', label: 'Expired' },
  { id: 'trial', label: 'Trial' },
];

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCurrency(amount: number, currency: string = 'INR'): string {
  try {
    const locale = currency === 'INR' ? 'en-IN' : currency === 'USD' ? 'en-US' : 'en-GB';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'USD' ? 2 : 0,
      minimumFractionDigits: currency === 'USD' ? 2 : 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

function formatSubscriptionPrice(pricing: AdminWorkspaceSubscriptionRow['pricing']): React.ReactNode {
  const currency = (pricing.display_currency || (pricing.billing_currency === 'USD' ? 'USD' : 'INR')).toUpperCase();
  const isUsd = currency === 'USD';
  const amount = isUsd
    ? (pricing.effective_monthly_price_usd ?? pricing.effective_monthly_price)
    : (pricing.effective_monthly_price_inr ?? pricing.effective_monthly_price);
  const base = isUsd
    ? (pricing.base_monthly_price_usd ?? pricing.base_monthly_price)
    : (pricing.base_monthly_price_inr ?? pricing.base_monthly_price);
  const showStrike =
    pricing.has_override && base !== amount && pricing.monthly_subscription_price_override != null;

  return (
    <>
      {showStrike && (
        <span className="text-xs text-admin-400 line-through mr-1">{formatCurrency(base, currency)}</span>
      )}
      <span className="font-medium text-admin-900">{formatCurrency(amount, currency)}</span>
    </>
  );
}

export const SubscriptionsPage: React.FC = () => {
  const basePath = useAdminBasePath();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<AdminWorkspaceSubscriptionRow[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<SubscriptionTab>('active');
  const [planFilter, setPlanFilter] = useState('');
  const [overrideFilter, setOverrideFilter] = useState('');
  const [editRow, setEditRow] = useState<AdminWorkspaceSubscriptionRow | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchRows = async () => {
      try {
        setLoading(true);
        setError('');
        const params: Record<string, unknown> = { page, limit, tab: activeTab };
        if (search.trim()) params.search = search.trim();
        if (planFilter) params.plan_name = planFilter;
        if (overrideFilter === 'yes') params.has_override = true;
        if (overrideFilter === 'no') params.has_override = false;

        const res = await fastapiService.getSubscriptions(params);
        setRows((res.data as AdminWorkspaceSubscriptionRow[]) || []);
        setTotal(res.pagination?.total || 0);
      } catch (e: any) {
        setError(e?.message || 'Failed to load subscriptions');
      } finally {
        setLoading(false);
      }
    };
    fetchRows();
  }, [page, limit, search, activeTab, planFilter, overrideFilter, refreshKey]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const handleSaved = () => {
    setRefreshKey((k) => k + 1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Subscriptions</h1>
        <p className="mt-1 text-sm text-admin-600">
          All workspaces with renewal dates, grace periods, and subscription pricing
        </p>
      </div>

      <div className="admin-card p-6">
        <div className="border-b border-admin-200 mb-4">
          <nav className="flex gap-6 -mb-px">
            {SUBSCRIPTION_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setPage(1);
                  setActiveTab(tab.id);
                }}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-admin-500 hover:text-admin-700 hover:border-admin-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <input
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Search workspace name…"
            className="admin-input flex-1 min-w-[200px] max-w-md"
          />
          <select
            value={planFilter}
            onChange={(e) => {
              setPage(1);
              setPlanFilter(e.target.value);
            }}
            className="admin-input w-40"
          >
            {PLAN_OPTIONS.map((p) => (
              <option key={p || 'all'} value={p}>
                {p ? p.charAt(0).toUpperCase() + p.slice(1) : 'All plans'}
              </option>
            ))}
          </select>
          <select
            value={overrideFilter}
            onChange={(e) => {
              setPage(1);
              setOverrideFilter(e.target.value);
            }}
            className="admin-input w-40"
          >
            <option value="">All pricing</option>
            <option value="yes">Has discount</option>
            <option value="no">Standard pricing</option>
          </select>
        </div>

        {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-admin-200">
            <thead className="bg-admin-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Workspace</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Owner</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Plan</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Renewal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Grace end</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Price / mo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-admin-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-admin-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-4 text-admin-500">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-4 text-admin-500">
                    No {activeTab} subscriptions found
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  return (
                    <tr key={row.workspace.id} className="hover:bg-admin-50">
                      <td className="px-4 py-4 text-sm">
                        <div className="font-medium text-admin-900">{row.workspace.name}</div>
                        {!row.workspace.is_active && (
                          <span className="text-xs text-admin-500">Inactive</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-admin-700">
                        <div>{row.workspace.owner?.email || '—'}</div>
                        {row.workspace.owner?.full_name && (
                          <div className="text-xs text-admin-500">{row.workspace.owner.full_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-admin-700 capitalize">
                        {row.subscription?.plan_name || '—'}
                        {row.subscription?.autopay_enabled && (
                          <div className="text-xs text-green-700 normal-case">
                            Autopay {row.subscription.autopay_status || 'on'}
                            {row.subscription.autopay_currency
                              ? ` (${row.subscription.autopay_currency})`
                              : ''}
                          </div>
                        )}
                        {!row.subscription?.autopay_enabled &&
                          String(row.subscription?.autopay_status || '').toLowerCase() === 'halted' && (
                            <div className="text-xs text-amber-700 normal-case">Autopay halted</div>
                          )}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <SubscriptionStatusBadge state={row.computed.computed_state} />
                      </td>
                      <td className="px-4 py-4 text-sm text-admin-700">
                        {formatDate(row.computed.renewal_date || row.subscription?.current_period_end)}
                        {row.computed.days_until_renewal != null && row.computed.computed_state === 'active' && (
                          <div className="text-xs text-admin-500">{row.computed.days_until_renewal}d left</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-admin-700">
                        {formatDate(row.computed.grace_end_effective)}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {formatSubscriptionPrice(row.pricing)}
                        {row.pricing.annual_discount_percentage > 0 && (
                          <div className="text-xs text-green-700">
                            {row.pricing.annual_discount_percentage}% annual
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800 font-medium"
                            onClick={() => setEditRow(row)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Edit
                          </button>
                          <Link
                            to={`${basePath}/invoices?workspace_id=${row.workspace.id}`}
                            className="text-admin-700 hover:text-admin-900"
                          >
                            Invoices
                          </Link>
                          <Link
                            to={`${ENTERPRISE_ADMIN_BASE}/workspaces/${row.workspace.id}`}
                            className="text-admin-700 hover:text-admin-900"
                          >
                            Workspace
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-admin-600">
            Page {page} of {totalPages} · {total} workspaces
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="admin-button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <button
              type="button"
              className="admin-button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {editRow && (
        <SubscriptionEditModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};
